import React, { useEffect, useMemo, useState,useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Pagination from "react-bootstrap/Pagination";
import KarmaApiService from '../services/karmaApi'
import {useMsal} from '@azure/msal-react';
import { utils, writeFile,read } from 'xlsx';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Multiselect from 'multiselect-react-dropdown';
import { useJobs } from '../contexts/JobsContext';
import { getFileNameFromHeaders, saveBlobAsFile, downloadWithRetry } from '../utils/downloadFile';

export default function ImportExport() {
  const normalizeValues = (value) => (value || '').toString().trim().toLowerCase();
  const parseCsvEnv = (value) =>
    (value || '')
      .split(',')
      .map((entry) => normalizeValues(entry))
      .filter(Boolean);

  const allowedImportUsernames = parseCsvEnv(process.env.REACT_APP_KARMA_IMPORT_ALLOWED_USERNAMES || '');
  const allowedImportAadGroupIds = parseCsvEnv(
    process.env.REACT_APP_KARMA_IMPORT_ALLOWED_AAD_GROUP_IDS || '0a73cf97-3e65-4b50-86cb-d14d8d871ac3'
  );

  const [state, setState] = useState({
    data: [],
    limit: 100,
    count: 0,
    activePage: 0,
    next: 1,
    backData:[],
    loading: false
  });
  const [importData, setImportData] = useState({});
  const [modalText,setModalText] = useState();
  const [recordCount,setRecordCount] = useState({applied:0,total:0});
  const [exportRecordLimit, setExportRecordLimit] = useState('all');
  const [includeContactStats, setIncludeContactStats] = useState(false);
  const [draftFilters, setDraftFilters] = useState({
    '[last_name]': '',
    '[addresses][street]': '',
    '[addresses][postal_code]': ''
  });
  const [includeOptions, setIncludeOptions] = useState([]);
  const [projectNameOptions, setProjectNameOptions] = useState([]);
  const [projectNameOptionsLoading, setProjectNameOptionsLoading] = useState(true);
  const [allContactStatusOptions, setAllContactStatusOptions] = useState([]);
  const [allContactStageOptions, setAllContactStageOptions] = useState([]);
  const [contactStatusOptions, setContactStatusOptions] = useState([]);
  const [contactStageOptions, setContactStageOptions] = useState([]);
  const [statusFilterMode, setStatusFilterMode] = useState('exclude');
  const [stageFilterMode, setStageFilterMode] = useState('exclude');
  const msProjects = useRef({options:[], selectedValues:[]});
  const msExcludeStatuses = useRef({options:[], selectedValues:[]});
  const msExcludeStages = useRef({options:[], selectedValues:[]});
  const [activeExportJobId, setActiveExportJobId] = useState(null);
  // The shared toast tray only has something to show once a job exists —
  // if starting the export itself fails (no jobId ever registered), there's
  // nothing in the tray to surface that, so this is shown inline instead
  // (the pre-migration implementation had the same inline error for this case).
  const [exportStartError, setExportStartError] = useState('');
  const searchFilters = useRef({});
  const facetsQueryKeyRef = useRef('');
  const { instance } = useMsal();
  const { jobs, trackJob, getDownloader } = useJobs();
  const activeExportJob = jobs.find((job) => job.jobId === activeExportJobId);
  const isExporting = activeExportJob ? (activeExportJob.status === 'queued' || activeExportJob.status === 'processing') : false;
  const activeAccount = instance.getActiveAccount();
  const settings = useRef([]);
  const importRef = useRef([]);
  const loadRequestRef = useRef(0);
  const [modalShow,setModalShow] = useState(false);
  const canImportToKarma = useMemo(() => {
    const accountUsername = normalizeValues(
      activeAccount?.username ||
      activeAccount?.idTokenClaims?.preferred_username ||
      activeAccount?.idTokenClaims?.upn ||
      activeAccount?.idTokenClaims?.email
    );
    const accountGroups = Array.isArray(activeAccount?.idTokenClaims?.groups)
      ? activeAccount.idTokenClaims.groups.map((groupId) => normalizeValues(groupId))
      : [];

    const hasAllowedGroup =
      allowedImportAadGroupIds.length > 0 &&
      allowedImportAadGroupIds.some((groupId) => accountGroups.includes(groupId));
    const hasAllowedUsername =
      allowedImportUsernames.length > 0 && allowedImportUsernames.includes(accountUsername);

    return hasAllowedGroup || hasAllowedUsername;
  }, [activeAccount, allowedImportAadGroupIds, allowedImportUsernames]);

  const handleImportClick = (e) => {
    if (!canImportToKarma) return;
    if (importRef.current.value) importRef.current.value = ''
    importRef.current.click()
  };
  const ms = useRef({options:[],selectedValues:[]});

  useEffect(() => {
    const loadSettings = async () => {
      setProjectNameOptionsLoading(true);
      const settingsResponse = await KarmaApiService.getSettings(instance).catch(function (error) {
        // Global auth errors are handled by the app-wide interceptor
        // This catch handler is only for non-auth errors
        console.error('[Karma Settings] Load failed:', error);
        return null;
      });

      if (!settingsResponse?.data?.settings?.fields?.results) {
        console.error('[Karma] No fields returned from settings API');
        setProjectNameOptions([]);
        setProjectNameOptionsLoading(false);
        return;
      }

      settings.current = settingsResponse;
      const fields = settingsResponse.data.settings.fields.results;

      const list = [];
      fields.forEach((m) => {
        if (m.field_type_id == 2 || m.field_type_id == 5) {
          list.push({name:m.name,id:m.id,field_type_id:m.field_type_id});
        }
      });
      ms.current.options = list;
      setIncludeOptions(list);

      const projectField = fields.find((field) => field.id == 228901) || 
                            fields.find((field) => (field.name || '').toLowerCase() === 'project name') ||
                            fields.find((field) => (field.name || '').toLowerCase().includes('project'));
      
      if (!projectField) {
        console.error('[Karma] Project field not found. Available fields:', fields.map(f => ({id: f.id, name: f.name, label: f.label})));
        setProjectNameOptions([]);
      } else if (!projectField.options || projectField.options.length === 0) {
        console.warn('[Karma] Project field found but has no options', {id: projectField.id, name: projectField.name, options: projectField.options});
        setProjectNameOptions([]);
      } else {
        console.log('[Karma] Project field loaded with', projectField.options.length, 'options');
        setProjectNameOptions(projectField.options);
      }
      setProjectNameOptionsLoading(false);
    };

    loadSettings();

    const loadEnumSettings = async () => {
      const enumResponse = await KarmaApiService.getEnumSettings(instance).catch((error) => {
        console.error('[Karma EnumSettings] Load failed:', error);
        return null;
      });
      const statuses = enumResponse?.data?.settings?.contact_statuses?.results || [];
      const stages = enumResponse?.data?.settings?.contact_stages?.results || [];
      const mappedStatuses = statuses.map((s) => ({ name: s.name, id: s.id }));
      const mappedStages = stages.map((s) => ({ name: s.name, id: s.id }));
      setAllContactStatusOptions(mappedStatuses);
      setAllContactStageOptions(mappedStages);
      setContactStatusOptions(mappedStatuses);
      setContactStageOptions(mappedStages);
    };
    loadEnumSettings();
  }, [instance]);

  useEffect(() => { // initial run
    // axios
    //   .get(
    //     `https://jsonplaceholder.typicode.com/posts?_page=1&_limit=${state.limit}`
    //   )
    //   .then((res) => {
    //     setState((prev) => ({
    //       ...prev,
    //       data: res.data
    //     }));
    //   })
    //   .catch((error) => console.log(error));
  }, [state.limit]);



  const handleImport = ($event) => {
    if (!canImportToKarma) return;
    //console.log("here");
    const files = $event.target.files;
    var rows;
    var columnNames;
    if (files.length) {
        const file = files[0];
        setImportData((prev) => ({
          ...prev,
          name : $event.target.files[0].name
        }));
          
        
        const reader = new FileReader();
        reader.onload = (event) => {
          const ab = event.target.result;
          /* Parse file */
     //     console.log("here")
          var wb = read(ab, {dense: true});
          var sheets;
          sheets = wb.SheetNames;
       //   console.log(wb);
          if (sheets.length) {
              rows = utils.sheet_to_json(wb.Sheets[sheets[0]], {defval:"" });
              const [columns] = utils.sheet_to_json(wb.Sheets[sheets[0]], { header: 1, defval:"" });
              columnNames = columns;
              validateData(rows);
              setImportData((prev) => ({
                ...prev,
                rows: rows
              }));
         //     console.log(rows);
          }
        }
        reader.readAsArrayBuffer(file);
    }
}

function validateData(data){
  var stringy ="";
  var keyNames = Object.keys(data[0]);
  console.log(keyNames);
  for(let z=0; z< keyNames.length;z++)
  {
  let splited = keyNames[z].split('|');

  if (splited[0].toLocaleLowerCase()=="custom")
  {
    var result = settings.current.data.settings.fields.results.find(obj => {
      return obj.name.toLocaleLowerCase() == splited[1].toLocaleLowerCase()
    })
    console.log(result);
    if (!result) 
    {
      stringy+= "Column '" + splited[1] + "' is not a recgonised custom column\n\n"
      continue;
    }

    for(let x=0; x < data.length; x++){
      //console.log(splited[2]);
      if (data[x][keyNames[z]].length < 1) continue;
      if(result.field_type_id == "2"){  // select list
      
      var result2 = result.options.find(obj => {
        return obj.name.toLocaleLowerCase() == data[x][keyNames[z]].toLocaleLowerCase()
      })
    //  console.log(result2);
      if (!result2) 
    {
      stringy+= "Column " + keyNames[z] +", Row " + (x+1) + ", Value '"+data[x][keyNames[z]]+"' is not a Valid dropdown option\n\n"
      continue;
    }
      }else{
        //console.log(splited[0])
        var result = settings.current.data.settings.fields.results.find(obj => {
          return obj.name == splited[1]
        })
      }
    }
  //  backdata[]
  }else{

    var result = settings.current.data.settings.fields.results.find(obj => {
      return obj.name == splited[0]
    })
    console.log(result);

  }

  }
 // console.log(stringy);
  if (stringy.length == 0){stringy = "OK.."}
  setModalShow(true);
  setModalText(stringy);
}

  const PaginationComponent = (next) =>{
    let numbers=[];
    console.log(next);
    console.log("count: "+state.count +" limit: " + state.limit)
    for (let number = next; number <= (state.count/state.limit) && number <= next+8; number++){
      console.log(number);
      numbers.push(
        <Pagination.Item
          key={number}
          active={number == state.activePage}
          onClick={() => {handlePageChange(number)}}
        >
        {number}
        </Pagination.Item>
      )
    }
    return numbers
  }

  function sortOrder(contact){
    var result1 = contact.field_values.filter((v,i,a)=>a.findIndex(v2=>(v.field_parent_id === v2.field_parent_id))===i)
        
    var output = [...result1];
    output = output.filter(function( obj ) {
        return obj.field_type_id == 2;
    });
    contact.field_values = contact.field_values.filter(function( obj ) {
        return obj.field_type_id !== 2;
    });
    contact.field_values = [...contact.field_values,...output];
    contact.field_values.sort((a,b) => a.id - b.id);
    return contact;
  }

  const handlePageChange = async (pageNumber) => {
    var nextIn = state.next;
    if (pageNumber>=state.next + 8) nextIn = state.next +1
    if (pageNumber!==1 && pageNumber <= state.next) nextIn = state.next -1
    await loadData(pageNumber,pageNumber);
    setState((prev) => ({
      ...prev,
      activePage: pageNumber,
      next : nextIn
    }));
  };

  const filter =() => {
      searchFilters.current = { ...draftFilters };
      setState({
        data: [],
        limit: 100,
        count: 0,
        activePage: 1,
        next: 1,
        backData:[],
        loading: true
      })
      loadData(1,1,true);
  }

  function msProjectsOnSelect(selectedList, selectedItem) {
    msProjects.current.selectedValues.push(selectedItem);
  }

  function msProjectsOnRemove(selectedList, removedItem) {
    msProjects.current.selectedValues = msProjects.current.selectedValues.filter(item => item.id !== removedItem.id);
  }

  function msExcludeStatusesOnSelect(selectedList, selectedItem) {
    msExcludeStatuses.current.selectedValues.push(selectedItem);
  }

  function msExcludeStatusesOnRemove(selectedList, removedItem) {
    msExcludeStatuses.current.selectedValues = msExcludeStatuses.current.selectedValues.filter(item => item.id !== removedItem.id);
  }

  function msExcludeStagesOnSelect(selectedList, selectedItem) {
    msExcludeStages.current.selectedValues.push(selectedItem);
  }

  function msExcludeStagesOnRemove(selectedList, removedItem) {
    msExcludeStages.current.selectedValues = msExcludeStages.current.selectedValues.filter(item => item.id !== removedItem.id);
  }

  const appendExcludeFilters = (baseQueries = '', options = {}) => {
    const includeStatusFilter = options.includeStatusFilter !== false;
    const includeStageFilter = options.includeStageFilter !== false;
    let queries = baseQueries;
    const excludeStatusIds = msExcludeStatuses.current.selectedValues.map((s) => s.id);
    const excludeStageIds = msExcludeStages.current.selectedValues.map((s) => s.id);
    const statusOperator = statusFilterMode === 'contains' ? 'or' : 'not';
    const stageOperator = stageFilterMode === 'contains' ? 'or' : 'not';

    if ((includeStatusFilter && excludeStatusIds.length > 0) || (includeStageFilter && excludeStageIds.length > 0)) {
      queries += '&filters[advanced]=true';
    }

    if (includeStatusFilter && excludeStatusIds.length > 0) {
      queries += `&filters[lead_status_id][operator]=${statusOperator}`;
      for (const id of excludeStatusIds) {
        queries += `&filters[lead_status_id][values][]=${id}`;
      }
    }

    if (includeStageFilter && excludeStageIds.length > 0) {
      queries += `&filters[lead_process_id][operator]=${stageOperator}`;
      for (const id of excludeStageIds) {
        queries += `&filters[lead_process_id][values][]=${id}`;
      }
    }

    return { queries, excludeStatusIds, excludeStageIds };
  }

  const buildFilterQueries = (filters = {}) => {
    let queries = '';
    for (const name in filters) {
      const value = filters[name];
      if ((value ?? '').toString().length > 0) {
        queries += `&filters${name}=${encodeURIComponent(value)}`;
      }
    }
    return queries;
  }

  const toNumericId = (value) => {
    if (value && typeof value === 'object') {
      if (Number.isFinite(Number.parseInt(value.id, 10))) {
        return Number.parseInt(value.id, 10);
      }
      return null;
    }
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  const refreshFilterOptionListsFromFacets = async (baseQueries, projectOptionIds, hasActiveFilters, requestId) => {
    if (!hasActiveFilters) {
      facetsQueryKeyRef.current = '';
      setContactStatusOptions(allContactStatusOptions);
      setContactStageOptions(allContactStageOptions);
      return;
    }

    const normalizedProjectIds = [...projectOptionIds].map((id) => Number.parseInt(id, 10)).filter((id) => Number.isFinite(id)).sort((a, b) => a - b);
    const statusFacetQuery = appendExcludeFilters(baseQueries, { includeStatusFilter: false, includeStageFilter: true }).queries;
    const stageFacetQuery = appendExcludeFilters(baseQueries, { includeStatusFilter: true, includeStageFilter: false }).queries;
    const queryKey = `${statusFacetQuery}::${stageFacetQuery}::${normalizedProjectIds.join(',')}`;
    if (facetsQueryKeyRef.current === queryKey) {
      return;
    }

    const [statusFacetsResponse, stageFacetsResponse] = await Promise.all([
      KarmaApiService.contactsFacets({ queries: statusFacetQuery, projectOptionIds }, instance).catch((error) => {
        console.error('[Karma Facets][Status] Load failed:', error);
        return null;
      }),
      KarmaApiService.contactsFacets({ queries: stageFacetQuery, projectOptionIds }, instance).catch((error) => {
        console.error('[Karma Facets][Stage] Load failed:', error);
        return null;
      })
    ]);
    if (!statusFacetsResponse || !stageFacetsResponse) return;
    if (requestId !== loadRequestRef.current) return;

    facetsQueryKeyRef.current = queryKey;
    const statusIdsInResults = new Set(
      (statusFacetsResponse?.data?.statusCounts || [])
        .map((item) => toNumericId(item?.id))
        .filter((id) => id !== null)
    );
    const stageIdsInResults = new Set(
      (stageFacetsResponse?.data?.stageCounts || [])
        .map((item) => toNumericId(item?.id))
        .filter((id) => id !== null)
    );

    const selectedStatusIds = new Set(
      msExcludeStatuses.current.selectedValues
        .map((option) => toNumericId(option?.id))
        .filter((id) => id !== null)
    );
    const selectedStageIds = new Set(
      msExcludeStages.current.selectedValues
        .map((option) => toNumericId(option?.id))
        .filter((id) => id !== null)
    );

    const statusOptionsFiltered = allContactStatusOptions.filter((option) => {
      const id = toNumericId(option?.id);
      return id !== null && (statusIdsInResults.has(id) || selectedStatusIds.has(id));
    });
    const stageOptionsFiltered = allContactStageOptions.filter((option) => {
      const id = toNumericId(option?.id);
      return id !== null && (stageIdsInResults.has(id) || selectedStageIds.has(id));
    });

    setContactStatusOptions(statusOptionsFiltered);
    setContactStageOptions(stageOptionsFiltered);
  }

  const loadData = async (page, active, resetBackData = false) =>{
    const requestId = ++loadRequestRef.current;
    const baseQueries = buildFilterQueries(searchFilters.current);
    var queries = baseQueries;

        const builtFilters = appendExcludeFilters(queries);
        queries = builtFilters.queries;

        const projectOptionIds = msProjects.current.selectedValues.map((p) => p.id);
      KarmaApiService.contactsFiltered({queries:queries, projectOptionIds},page,state.limit,instance)
        .then((res) => {
          if (requestId !== loadRequestRef.current) return;
       //   console.log(res.data.list);
          for (let x=0; x< res.data.list.length;x++){
            res.data.list[x] = sortOrder(res.data.list[x]);
          }

          const hasActiveFilters = queries.length > 0 || projectOptionIds.length > 0;
          refreshFilterOptionListsFromFacets(baseQueries, projectOptionIds, hasActiveFilters, requestId);

            setState((prev) => {
              return {
                ...prev,
                data: res.data.list || [],
                backData: resetBackData ? [] : prev.backData,
                count: res.data.count,
                loading:false
              };
            });
          })
          .catch((error) => {
            if (requestId !== loadRequestRef.current) return;
            refreshFilterOptionListsFromFacets('', [], false, requestId);
            setState((prev) => ({ 
              ...prev,
              loading:false
            }));
            console.log(error)
          }
          );
  }

  const exportList =() => {
          for(let z=0; z< state.backData.length;z++)
          {
          for (let x =0; x < state.backData[z]?.field_values.length; x++)
          {
            
            console.log(state.backData[z]);
            let item = state.backData[z].field_values[x];
            if(item.field_parent_id){
            var result = settings.current.data.settings.fields.results.find(obj => {
              return obj.id == item.field_parent_id??item.field_id
            })
            //console.log(result);
            var result2 = result.options.find(obj => {
              return obj.id == item.value
            })
            //console.log(result2);
            state.backData[z]["custom|"+result.name] = result2?.name;     //+"|"+result.id] = result2.name;
            }else{
              var result = settings.current.data.settings.fields.results.find(obj => {
                return obj.id == item.field_id
              })
              console.log(result);
              state.backData[z]["custom|"+result.name] = item.value; //   +"|"+result.id] = item.value;
            }
          //  backdata[]
          }
          if (state.backData[z].addresses[0]){
              state.backData[z]["address|street"] = state.backData[z].addresses[0].street
              state.backData[z]["address|city"] = state.backData[z].addresses[0].city
              state.backData[z]["address|postal_code"] = state.backData[z].addresses[0].postal_code
          }
          if (state.backData[z].emails[0]){
              state.backData[z]["email|email"] = state.backData[z].emails[0].email
          }
          }

          var obj = structuredClone(state.backData);
          for(let z=0; z< obj.length;z++)
          {
          delete obj[z].emails
          delete obj[z].addresses
          delete obj[z].field_values
          }
          for (let z = 0; z < ms.current.selectedValues.length; z++){

            if (!obj.some(o => o.hasOwnProperty("custom|"+ms.current.selectedValues[z].name))){//+"|"+ms.current.selectedValues[z].id))) {
              obj[0]["custom|"+ms.current.selectedValues[z].name] = "";//+"|"+ms.current.selectedValues[z].id] = "";
            }
          }

          const wb = utils.book_new();
          const ws = utils.json_to_sheet([]);
          utils.sheet_add_json(ws, obj, { origin: 'A1', skipHeader: false });
          utils.book_append_sheet(wb, ws, 'Report');
          writeFile(wb, 'Karma Export.xlsx'); 
 }

 const exportFromBackend = async (mode = 'all') => {
  setExportStartError('');
  const parseLimit = (value) => {
    if (value === 'all') return null;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const selectedLimit = parseLimit(exportRecordLimit);
  const maxRecords = mode === 'current' ? null : selectedLimit;
  const projectOptionIds = mode === 'current' ? msProjects.current.selectedValues.map((p) => p.id) : [];
  let queries = mode === 'current' ? buildFilterQueries(draftFilters) : '';
  const builtFilters = appendExcludeFilters(queries);
  queries = builtFilters.queries;
  const excludeStatusIds = builtFilters.excludeStatusIds;
  const excludeStageIds = builtFilters.excludeStageIds;
  const excludeStatusIdsForPayload = statusFilterMode === 'exclude' ? excludeStatusIds : [];
  const excludeStageIdsForPayload = stageFilterMode === 'exclude' ? excludeStageIds : [];
  const contactIds = [];
  let filteredCount = null;

  if (mode === 'current') {
    searchFilters.current = { ...draftFilters };
    try {
      const countCheckTimeout = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('count check timed out')), 5000);
      });
      const countResponse = await Promise.race([
        KarmaApiService.contactsFiltered({ queries, projectOptionIds }, 1, 1, instance),
        countCheckTimeout
      ]);
      if (Number.isFinite(countResponse?.data?.count)) {
        filteredCount = countResponse.data.count;
        setState((prev) => ({
          ...prev,
          count: countResponse.data.count
        }));
      }
    } catch (countError) {
      console.log('[Karma Export] Unable to refresh filtered count before export:', countError);
    }
  }

  // Starts the export on the backend (unchanged endpoint) and hands
  // tracking off to the shared JobsContext — one poller for every
  // background job in the app instead of a bespoke setInterval here, and
  // progress now shows in the persistent toast tray instead of an inline
  // bar that disappears the moment this page is navigated away from.
  try {
    const startResponse = await KarmaApiService.exportReadableContactsStart(instance, {
      maxRecords,
      queries,
      contactIds,
      projectOptionIds,
      excludeStatusIds: excludeStatusIdsForPayload,
      excludeStageIds: excludeStageIdsForPayload,
      includeContactStats
    });
    const jobId = startResponse?.data?.jobId;

    if (!jobId) {
      throw new Error('No export job id returned');
    }

    setActiveExportJobId(jobId);

    const label = mode === 'current'
      ? `Karma export (current filter values${Number.isFinite(filteredCount) ? `, ${filteredCount} records` : ''})`
      : `Karma export${maxRecords ? ` (${maxRecords} records max)` : ' (all records, no filters)'}`;

    trackJob(jobId, {
      label,
      downloadable: true,
      // Preserves the pre-migration behavior of auto-downloading as soon as
      // the export completes; the manual "Download" button in the toast
      // tray (via the `download` override below) still works as a retry.
      onComplete: async ({ status }) => {
        if (status !== 'completed') return;
        try {
          // Same bounded transient-error retry the manual download button
          // (and the pre-migration implementation) uses, so a 502/503/504
          // blip doesn't silently skip the auto-download and rely on the
          // user noticing the manual button instead.
          const response = await downloadWithRetry(getDownloader(jobId));
          saveBlobAsFile(response.data, getFileNameFromHeaders(response.headers, `${label}.xlsx`));
        } catch (error) {
          console.log('[Karma Export] Auto-download failed after retries, manual retry still available:', error);
        }
      },
      // Karma export already has its own status/download endpoints from
      // before this migration — reuse them rather than moving this feature
      // onto the generic /api/jobs routes.
      fetchStatus: async () => {
        const response = await KarmaApiService.exportReadableContactsStatus(jobId, instance);
        const data = response?.data || {};
        if (data.page && data.maxPages) {
          data.message = `${data.message || data.phase || ''} (page ${data.page}/${data.maxPages})`;
        }
        return { ...response, data };
      },
      download: (onDownloadProgress) => KarmaApiService.exportReadableContactsDownload(jobId, instance, onDownloadProgress)
    });
  } catch (error) {
    // No jobId ever existed here, so the shared toast tray has nothing to
    // show for this — surface it inline instead (the pre-migration
    // implementation had the equivalent exportProgress.error for this case).
    setExportStartError('Export failed to start. Please try again.');
    console.log(error);
  }
 }

 function Spinner() {
  return (
      <>
          <div className="d-flex justify-content-center">
              <div className="spinner-border text-warning" role="status">
                  <span className="sr-only">Loading...</span>
              </div>
          </div>
      </>
  )
}

const showModal = () => {
  setModalShow(true);
};

const hideModal = () => {
  setModalShow(false);
  setModalText('');
};

const importKarma = async() =>{
  if (!canImportToKarma) return;
  var data = structuredClone(importData.rows);
  var stringy ="";
  var keyNames = Object.keys(data[0]);
  console.log(keyNames);
  for(let z=0; z< keyNames.length;z++)
  {
  let splited = keyNames[z].split('|');

  if (splited[0].toLocaleLowerCase() == "custom") // custom column
  {
    var result = settings.current.data.settings.fields.results.find(obj => {
      return obj.name.toLocaleLowerCase() == splited[1].toLocaleLowerCase()
    })

    console.log(result);
    console.log(data);

    if (!result) continue;
    //console.log(result);
    for(let x=0; x < data.length; x++){ // this column each row
      //console.log(splited[2]);
      
      if(result.field_type_id == "2"){  // select list
      
      var result2 = result.options.find(obj => {
        return obj.name.toLocaleLowerCase() == data[x][keyNames[z]].toLocaleLowerCase()
      })
      
      if (result2) 
        {
          if (!data[x].field_values) data[x].field_values = [];
          let fv =  data[x].field_values?.find(obj => obj.field_parent_id === result.id);
          if (fv){
            fv.value = result2.id
          }else{
          data[x].field_values.push({field_parent_id:result.id,field_id:result2.id,value:result2.id.toString()});
          }
        }else{
          continue;
        }
      }else{
        //console.log(splited[0])
        var result3 = settings.current.data.settings.fields.results.find(obj => {
          return obj.name.toLocaleLowerCase() == splited[1].toLocaleLowerCase()
        })
     //   console.log(result);
        if ((!result3)||data[x][keyNames[z]].toString().length==0) continue;
        if (!data[x].field_values) data[x].field_values = [];
        let fv =  data[x].field_values?.find(obj => obj.field_id === result3.id);
        if (fv){
          fv.value = result2.id
        }else{
        data[x].field_values.push({field_id:result3.id,value:data[x][keyNames[z]].toString()});
        }
      }
    }
  }else if (splited[0].toLocaleLowerCase() == "address"){   /////////////////////////////todo normal fields  and addresses 
    for(let x=0; x < data.length; x++){ 
      if (data[x][keyNames[z]].toString().length < 1) continue;
      if(!data[x].addresses){
        data[x].addresses =[];
        data[x].addresses.push({street:'',city:'',postal_code:'',address_type_id:1})
      }
      switch (splited[1].toLocaleLowerCase()) {
        
        case 'street':
          data[x].addresses[0].street = data[x][keyNames[z]].toString()
          break;
          case 'city':
          data[x].addresses[0].city = data[x][keyNames[z]].toString()
          break;
          case 'postal_code':
          data[x].addresses[0].postal_code = data[x][keyNames[z]].toString()
          break;
        default:
          break;
      }
      }
    }else if(splited[0].toLocaleLowerCase() == "email"){
      for(let x=0; x < data.length; x++){ 
        if (data[x][keyNames[z]].toString().length < 1) continue;
        if(!data[x].emails){
          data[x].emails =[];
          data[x].emails.push({email:'',email_type_id:1})
        }
        if (splited[1].toLocaleLowerCase() == "email"){
          data[x].emails[0].email = data[x][keyNames[z]].toString()
        }
    }
    }
  
  }
      for(let y=0; y < data.length;y++){
        
        var res = await KarmaApiService.contactGet(data[y]['id'], instance).catch(function (error) {
          if (error.response) {
              console.log(error.response.data);
              console.log(error.response.status);
              console.log(error.response.headers);
          } else if (error.request) {
              console.log(error.request);
          } else {
              console.log('Error', error.message);
          }
        });    
        res.data = sortOrder(res.data);
        for (let r =0; r < data[y]?.field_values?.length; r++ ){ // this stops the same custom field being re added
            if (data[y].field_values[r].field_parent_id){
            let fv =  res.data.field_values?.find(obj => obj.field_parent_id === data[y].field_values[r].field_parent_id);
            if (fv) {data[y].field_values[r].id = fv.id}
            }else{
            let fv2 =  res.data.field_values?.find(obj => obj.field_id === data[y].field_values[r].field_id);
            if (fv2) {data[y].field_values[r].id = fv2.id}
            }
        }
        console.log(res.data);
        if (res.data.addresses && res.data.addresses.length > 0) data[y].addresses[0].id = res.data.addresses[0].id
        if (res.data.emails && res.data.emails.length > 0) data[y].emails[0].id = res.data.emails[0].id


        var clone = structuredClone(data[y]);
        var contactOut ={};
        contactOut.contact ={};
        contactOut.contact= clone;
        var id = data[y]['id'];
         id = id.toString();
        delete contactOut.contact['id'];
        delete contactOut.contact['avatar_url'];
        delete contactOut.contact['organization_id'];
        delete contactOut.contact['created_at'];
        delete contactOut.contact['created_by_id'];
        delete contactOut.contact['updated_at'];
        delete contactOut.contact['relationships'];
        delete contactOut.contact['important_dates'];
        delete contactOut.contact['industry_id'];
        delete contactOut.contact['company'];
        delete contactOut.contact['social_accounts'];
        delete contactOut.contact['websites'];
        delete contactOut.contact['phone_numbers'];
        delete contactOut.contact['permissions'];
        delete contactOut.contact['permission'];
        delete contactOut.contact['tag_list'];
        delete contactOut.contact['group_id'];
        delete contactOut.contact['lead_status_id'];
        delete contactOut.contact['lead_process_id'];
        delete contactOut.contact['note_last_added_at'];
        delete contactOut.contact['note_last_added_id'];
        delete contactOut.contact['referral_source_id'];

        var z = Object.keys(contactOut.contact).filter(function(k) {
          return k.indexOf('custom|') == 0;
        })

        for(let x = 0; x < z.length; x++)
        {
          delete contactOut.contact[z[x]]
        }

        var z = Object.keys(contactOut.contact).filter(function(k) {
          return k.indexOf('address|') == 0;
        })

        for(let x = 0; x < z.length; x++)
        {
          delete contactOut.contact[z[x]]
        }

        var z = Object.keys(contactOut.contact).filter(function(k) {
          return k.indexOf('email|') == 0;
        })
        for(let x = 0; x < z.length; x++)
        {
          delete contactOut.contact[z[x]]
        }
        for (let x = 0; x < contactOut?.contact['field_values']?.length; x++) {
            delete contactOut.contact['field_values'][x]['field_type_id']
        }

        delete contactOut.contact.notes
        
        await KarmaApiService.contactUpdate(id, contactOut, instance)
        setRecordCount({applied:y+1,total:data.length})
      }
      hideModal();
}


function msOnSelect(selectedList, selectedItem) {
  ms.current.selectedValues.push(selectedItem);
}

function msOnRemove(selectedList, removedItem) {
//  ms.current.selectedValues = selectedList;
ms.current.selectedValues = ms.current.selectedValues.filter(item => item.id !== removedItem.id);
}

  return (
    <>
    <Modal show={modalShow} onHide={hideModal} dialogClassName="modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>Import Validation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <>
            <textarea
        className="form-control"
        id="exampleFormControlTextarea1"
        rows="5"
        readOnly
        value={modalText}
      />
            </>
        </Modal.Body>
        <Modal.Footer>
          {
          (recordCount.applied == recordCount.total)?<>
          <Button variant="secondary" onClick={hideModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={importKarma} disabled ={(modalText?.length>4)}>
            Import to Karma
          </Button>
          </>:<>
          <span>{'Imported '+ recordCount.applied + ' of '+ recordCount.total}</span>
          </>
          }
        </Modal.Footer>
      </Modal>

    <div className="container py-4">
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h4 className="mb-0">Karma Import & Export</h4>
            <span className="text-muted small">Manage filters, export scope, and import actions in one place.</span>
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold">Include Columns</label>
            <Multiselect
              options={includeOptions}
              selectedValues={ms.current.selectedValues}
              onSelect={msOnSelect}
              onRemove={msOnRemove}
              displayValue="name"
            />
          </div>

          {/* Project Name filter on its own row at the top */}
          <div className="row g-3 align-items-end mb-2">
            <div className="col-md-4">
              <label className="form-label d-flex align-items-center gap-2">
                <span>Project Name</span>
                {projectNameOptionsLoading && (
                  <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true"></span>
                )}
              </label>
              {projectNameOptionsLoading ? (
                <input className="form-control" disabled placeholder="Loading projects..." />
              ) : (
                <Multiselect
                  options={projectNameOptions}
                  selectedValues={msProjects.current.selectedValues}
                  onSelect={msProjectsOnSelect}
                  onRemove={msProjectsOnRemove}
                  displayValue="name"
                  placeholder="Select projects..."
                  hidePlaceholder
                  avoidHighlightFirstOption
                />
              )}
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center justify-content-between gap-2">
                <label className="form-label mb-0">Statuses</label>
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: '120px' }}
                  value={statusFilterMode}
                  onChange={(e) => setStatusFilterMode(e.target.value)}
                >
                  <option value="exclude">Exclude</option>
                  <option value="contains">Contains</option>
                </select>
              </div>
              <Multiselect
                options={contactStatusOptions}
                selectedValues={msExcludeStatuses.current.selectedValues}
                onSelect={msExcludeStatusesOnSelect}
                onRemove={msExcludeStatusesOnRemove}
                displayValue="name"
                placeholder={statusFilterMode === 'contains' ? 'No contains values selected' : 'None excluded'}
                hidePlaceholder
                avoidHighlightFirstOption
              />
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center justify-content-between gap-2">
                <label className="form-label mb-0">Stages</label>
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: '120px' }}
                  value={stageFilterMode}
                  onChange={(e) => setStageFilterMode(e.target.value)}
                >
                  <option value="exclude">Exclude</option>
                  <option value="contains">Contains</option>
                </select>
              </div>
              <Multiselect
                options={contactStageOptions}
                selectedValues={msExcludeStages.current.selectedValues}
                onSelect={msExcludeStagesOnSelect}
                onRemove={msExcludeStagesOnRemove}
                displayValue="name"
                placeholder={stageFilterMode === 'contains' ? 'No contains values selected' : 'None excluded'}
                hidePlaceholder
                avoidHighlightFirstOption
              />
            </div>
          </div>

          {/* Other filters below */}
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className="form-control"
                id="[last_name]"
                value={draftFilters['[last_name]']}
                onChange={(v) => {
                  const { id, value } = v.target;
                  setDraftFilters((prev) => ({ ...prev, [id]: value }));
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Address</label>
              <input
                type="text"
                className="form-control"
                id="[addresses][street]"
                value={draftFilters['[addresses][street]']}
                onChange={(v) => {
                  const { id, value } = v.target;
                  setDraftFilters((prev) => ({ ...prev, [id]: value }));
                }}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Postcode</label>
              <input
                type="text"
                className="form-control"
                id="[addresses][postal_code]"
                value={draftFilters['[addresses][postal_code]']}
                onChange={(v) => {
                  const { id, value } = v.target;
                  setDraftFilters((prev) => ({ ...prev, [id]: value }));
                }}
              />
            </div>
            <div className="col-md-2 d-grid ms-auto">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => { filter(); }}
                disabled={state.loading}
              >
                {state.loading ? 'Filtering...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <input id='importFile' type='file' accept='.xlsx,.csv' ref={importRef} className='custom-file-input' onChange={(e) => handleImport(e)} style={{ display: 'none' }}></input>

      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
            <div className="d-flex align-items-center gap-2">
              {state.loading ? <Spinner></Spinner> : <span className="text-muted">{`Found ${state.count || 0} records`}</span>}
            </div>
            <div className="d-flex align-items-end flex-wrap gap-2">
              <div style={{minWidth:'170px'}}>
                <label className="form-label mb-1 small text-muted">Export All Limit</label>
                <select
                  className="form-select form-select-sm"
                  value={exportRecordLimit}
                  onChange={(e) => setExportRecordLimit(e.target.value)}
                  disabled={isExporting}
                >
                  <option value="all">All Records</option>
                  <option value="500">500</option>
                  <option value="1000">1000</option>
                  <option value="5000">5000</option>
                  <option value="10000">10000</option>
                </select>
              </div>

              <div
                className={`form-check d-flex align-items-center gap-2 border rounded px-2 py-1 ${includeContactStats ? 'border-success bg-success-subtle' : 'border-warning bg-warning-subtle'}`}
                style={{ minWidth: '280px' }}
              >
                <input
                  type="checkbox"
                  className="form-check-input mt-0"
                  id="includeContactStats"
                  checked={includeContactStats}
                  onChange={(e) => setIncludeContactStats(e.target.checked)}
                  disabled={isExporting}
                />
                <label className="form-check-label small" htmlFor="includeContactStats" title="Adds Last Contact and Times Contacted columns. Fetches contact history per record, so exports take longer.">
                  <strong>{includeContactStats ? 'Contact attempts included' : 'Include contact attempts'}</strong>
                  <span className="d-block text-muted" style={{ fontSize: '0.72rem' }}>
                    Adds Last Contact and Times Contacted columns
                  </span>
                </label>
              </div>

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => exportFromBackend('current')}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export Filtered'}
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => exportFromBackend('all')}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export All'}
              </button>
              <button
                onClick={(e) => handleImportClick(e)}
                className="btn btn-outline-secondary"
                disabled={!canImportToKarma}
                title={canImportToKarma ? '' : 'Import restricted to authorised users only'}
              >
                Import to Karma
              </button>
              {exportStartError && <small className="text-danger d-block mt-1">{exportStartError}</small>}
            </div>
          </div>

        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <strong>Results</strong>
        </div>
        <ul className="list-group list-group-flush">
          {state.data.map((item) => {
            const firstName = typeof item.first_name === 'string'
              ? item.first_name
              : (item.first_name?.name || '');
            const lastName = typeof item.last_name === 'string'
              ? item.last_name
              : (item.last_name?.name || '');
            const fullName = `${firstName} ${lastName}`.trim() || 'No name';
            const companyName = typeof item.company === 'string'
              ? item.company
              : (item.company?.name || '');
            const addressLine = (item.addresses?.length > 0)
              ? `${item.addresses[0].street || ''}${item.addresses[0].street && item.addresses[0].postal_code ? ', ' : ''}${item.addresses[0].postal_code || ''}`
              : '';
            return (
              <li key={item.id} className="list-group-item py-3">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <div className="fw-semibold" style={{fontSize:'1rem'}}>{fullName}</div>
                    {!!companyName && <div className="text-muted small">{companyName}</div>}
                    {!!addressLine && <div className="text-muted small mt-1">{addressLine}</div>}
                  </div>
                  <span className="badge bg-light text-dark border">ID: {item.KarmaContactID || item.id}</span>
                </div>
              </li>
            );
          })}
          {!state.data.length && !state.loading && (
            <li className="list-group-item text-muted">No results to display.</li>
          )}
        </ul>
      </div>

      <div className="d-flex justify-content-center mt-3">
        <Pagination>
          {PaginationComponent(state.next)}
        </Pagination>
      </div>
    </div>
    </>
  );
}
